import React from 'react'

import {
  Box,
  Button,
  Flex,
  HStack,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react'
import { Spacer } from 'src/components/__legacy__/spacer/spacer'
import { Add, MessageInformation } from 'src/components/icon'
import { ApiResponse } from 'src/lib/api-client'

import { LinkTable } from '../../../../../../../../components/LinkTable/LinkTable'
import { LoadingDots } from '../../../../../../../../components/LoadingDots/LoadingDots'
import {
  usePermission,
  useSelectedTrial,
} from '../../../../../../../../features/auth/context'
import { formatDate } from '../../../../../../../../utils/formatDate'
import { getEditIcfDocumentRoute } from '../../routes'
import { IcfDocumentRevisionStatusBadge } from '../components/icf-document-status-badge'
import { RequiredPatientSignBadge } from '../components/required-patient-sign-badge'
import { IcfDocumentType, icfDocumentTypeText } from '../entity'

type Props = {
  icfDocumentList: ApiResponse<'ListICFDocumentsItem'>[] | undefined
  selectedTrialHospitalUid: string | undefined
  shouldShowReAgreementConfirmButton: boolean
  navigateToAdd: () => void
  navigateToEdit: ({
    icfDocumentUid,
    latestRevisionUid,
  }: {
    icfDocumentUid: string
    latestRevisionUid: string
  }) => void
  onClickDocumentSetLink: (documentUid: string) => void
  onClickReAgreementConfirmButton: () => void
}

export const IcfDocumentListRenewal: React.FC<Props> = ({
  icfDocumentList,
  selectedTrialHospitalUid,
  shouldShowReAgreementConfirmButton,
  navigateToAdd,
  onClickDocumentSetLink,
  onClickReAgreementConfirmButton,
}) => {
  const { selectedTrial } = useSelectedTrial()
  const { hasPermission } = usePermission()
  return (
    <Flex direction="column" h="full">
      <HStack w="full" justify="right">
        <HStack spacing="16px">
          {shouldShowReAgreementConfirmButton && (
            <Button
              onClick={onClickReAgreementConfirmButton}
              colorScheme="green"
            >
              再同意確認
            </Button>
          )}
          {hasPermission('icfdocument:CreateDocument') && (
            <Button
              variant="outline"
              leftIcon={<Add />}
              onClick={navigateToAdd}
              disabled={selectedTrialHospitalUid === undefined}
            >
              新規文書登録
            </Button>
          )}
        </HStack>
      </HStack>
      <Box flex="1" w="full" overflow="auto" mt="3">
        {icfDocumentList ? (
          <LinkTable
            data={icfDocumentList}
            columns={[
              {
                key: 'isRequiredReAgreementConfirmation',
                renderHeader: null,
              },
              {
                key: 'status',
                renderHeader: 'ステータス',
              },
              {
                key: 'numberingId',
                renderHeader: '文書ID',
              },
              {
                key: 'name',
                renderHeader: '文書名',
              },
              {
                key: 'version',
                renderHeader: '版数',
              },
              {
                key: 'type',
                renderHeader: '文書の種類',
              },
              {
                key: 'publishedAt',
                renderHeader: '公開日時',
              },
              {
                key: 'isLinkedICFDocumentSet',
                renderHeader: '文書セット',
              },
            ]}
            linkTarget="name"
            renderRow={node => ({
              rowId: node.uid,
              columnValues: {
                isRequiredReAgreementConfirmation: {
                  renderColumn: node.isRequiredReAgreementConfirmation && (
                    <Box pos="relative" zIndex="1" w="max-content">
                      <Tooltip label="再同意確認要">
                        <Box color="green.500" w="max-content">
                          <MessageInformation size="12px" />
                        </Box>
                      </Tooltip>
                    </Box>
                  ),
                  tdProps: {
                    w: '12px',
                    p: '0',
                  },
                },
                status: {
                  renderColumn: (
                    <IcfDocumentRevisionStatusBadge status={node.status} />
                  ),
                  tdProps: {
                    minW: '120px',
                  },
                },
                numberingId: {
                  renderColumn: node.numberingId,
                  tdProps: {
                    minW: '100px',
                    color: node.status === 'Disabled' ? 'gray.400' : undefined,
                  },
                },
                name: {
                  renderColumn: node.name,
                  tdProps: {
                    minW: '160px',
                    color: node.status === 'Disabled' ? 'gray.400' : undefined,
                  },
                },
                version: {
                  renderColumn: (
                    <Stack direction="row" alignItems="center">
                      <Text whiteSpace="nowrap">{node.version}</Text>
                      {node.isRevising && (
                        <Text fontSize="xs" color="green.600">
                          （改版中）
                        </Text>
                      )}
                    </Stack>
                  ),
                  tdProps: {
                    minW: '140px',
                    color: node.status === 'Disabled' ? 'gray.400' : undefined,
                  },
                },
                type: {
                  renderColumn: (
                    <Stack direction="row" alignItems="center" gap="0">
                      <Text whiteSpace="nowrap">
                        {icfDocumentTypeText[node.type as IcfDocumentType]}
                      </Text>
                      {node.isRequiredPatientSign && (
                        <>
                          <Spacer size={8} horizontal />
                          <RequiredPatientSignBadge />
                        </>
                      )}
                    </Stack>
                  ),
                  tdProps: {
                    minW: '100px',
                    color: node.status === 'Disabled' ? 'gray.400' : undefined,
                  },
                },
                publishedAt: {
                  renderColumn:
                    node.publishedAt &&
                    formatDate(node.publishedAt, 'YYYY/MM/DD (ddd) HH:mm'),
                  tdProps: {
                    minW: '160px',
                    color: node.status === 'Disabled' ? 'gray.400' : undefined,
                  },
                },
                isLinkedICFDocumentSet: {
                  renderColumn: node.isLinkedICFDocumentSet && (
                    <Button
                      minW="unset"
                      variant="link"
                      onClick={e => {
                        e.stopPropagation()
                        onClickDocumentSetLink(node.uid)
                      }}
                    >
                      設定済
                    </Button>
                  ),
                  tdProps: {
                    minW: '100px',
                  },
                },
              },
            })}
            link={node =>
              getEditIcfDocumentRoute({
                trialUid: selectedTrial.uid,
                icfDocumentUid: node.uid,
                trialHospitalUid: selectedTrialHospitalUid,
              })
            }
          />
        ) : (
          <LoadingDots />
        )}
      </Box>
    </Flex>
  )
}
